/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {
  KafkaProducerPropertiesConfiguration,
  MessageEndpoint,
  MessageEndpointConfiguration,
  MessageEndpointNames,
  MessageEndpointTypes
} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {isValidEmail} from '@ion/core/src/utils/Validators';
import {FormState} from '@ion/dls/src/hooks/useFormState';

export enum MessageEndpointFormFields {
  TYPE = 'type',
  AWS_ACCESS_KEY = 'awsAccessKeyId',
  AWS_SECRET_ACCESS_KEY = 'awsSecretAccessKey',
  REGION = 'region',
  QUEUE_NAME = 'queueName',
  STREAM_NAME = 'streamName',
  BOOTSTRAP_SERVERS = 'bootstrapServers',
  KAFKA_TOPIC_NAME = 'topicName',
  USERNAME = 'username',
  PASSWORD = 'password',
  SECURITY_PROTOCOL = 'security.protocol',
  VALUE_SERIALIZER = 'value.serializer',
  SASL_MECHANISM = 'sasl.mechanism',
  ACKNOWLEDGMENTS = 'acks',
  SASL_JAAS_CONFIG = 'sasl.jaas.config',
  KEY_SERIALIZER = 'key.serializer',
  GCP_TOPIC_NAME = 'topicName',
  CLIENT_ID = 'clientId',
  PROJECT_ID = 'projectId',
  CLIENT_EMAIL = 'clientEmail',
  PRIVATE_KEY_ID = 'privateKeyId',
  CERT_URL = 'certUrl',
  PRIVATE_KEY = 'privateKey'
}

const KAFKA_KEYS_MAP = new Set([
  'security.protocol',
  'sasl.jaas.config',
  'value.serializer',
  'key.serializer',
  'sasl.mechanism',
  'client.id',
  'acks'
]);

const required    = {required: true};
const notRequired = {required: false};

export const getInitialKafkaFormState = (
  currentType: MessageEndpointTypes,
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const isEdit              = !!exisitingMessageEndpoint;
  const isKafkaPlainOrScram = getIsKafkaPlainOrScramType(currentType);
  return {
    [MessageEndpointFormFields.BOOTSTRAP_SERVERS] : {
      ...required,
      name  : MessageEndpointFormFields.BOOTSTRAP_SERVERS,
      value : exisitingMessageEndpoint?.bootstrapServers || ''
    },
    [MessageEndpointFormFields.KAFKA_TOPIC_NAME] : {
      ...required,
      name  : MessageEndpointFormFields.KAFKA_TOPIC_NAME,
      value : exisitingMessageEndpoint?.topicName || ''
    },
    [MessageEndpointFormFields.USERNAME] : {
      required : isKafkaPlainOrScram,
      name     : MessageEndpointFormFields.USERNAME,
      value    : exisitingMessageEndpoint?.username || ''
    },
    [MessageEndpointFormFields.PASSWORD] : {
      required : isKafkaPlainOrScram && !isEdit,
      name     : MessageEndpointFormFields.PASSWORD,
      value    : exisitingMessageEndpoint?.password || ''
    },
    ...getInitialKafkaProducerPropertyFormState(exisitingMessageEndpoint)
  };
};

export const getInitialStreamFormState = (
  currentType: MessageEndpointTypes,
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const isEdit   = !!exisitingMessageEndpoint;
  const isStream = currentType === MessageEndpointTypes.AWS_KINESIS;
  return {
    [MessageEndpointFormFields.AWS_ACCESS_KEY] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.AWS_ACCESS_KEY,
      value    : exisitingMessageEndpoint?.awsAccessKeyId || ''
    },
    [MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.AWS_SECRET_ACCESS_KEY,
      value    : exisitingMessageEndpoint?.awsSecretAccessKey || ''
    },
    [MessageEndpointFormFields.REGION] : {
      ...required,
      name  : MessageEndpointFormFields.REGION,
      value : exisitingMessageEndpoint?.region || ''
    },
    [MessageEndpointFormFields.STREAM_NAME] : {
      required : isStream,
      name     : MessageEndpointFormFields.STREAM_NAME,
      value    : isStream ? exisitingMessageEndpoint?.streamName || '' : null
    },
    [MessageEndpointFormFields.QUEUE_NAME] : {
      required : !isStream,
      name     : MessageEndpointFormFields.QUEUE_NAME,
      value    : !isStream ? exisitingMessageEndpoint?.queueName || '' : null
    }
  };
};

export const getInitialKafkaProducerPropertyFormState = (
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const {
    kafkaProducerProperties
  } = exisitingMessageEndpoint || {};

  return {
    [MessageEndpointFormFields.SECURITY_PROTOCOL] : {
      ...notRequired,
      name  : MessageEndpointFormFields.SECURITY_PROTOCOL,
      value : kafkaProducerProperties?.['security.protocol'] || ''
    },
    [MessageEndpointFormFields.VALUE_SERIALIZER] : {
      ...notRequired,
      name  : MessageEndpointFormFields.VALUE_SERIALIZER,
      value : kafkaProducerProperties?.['value.serializer'] || ''
    },
    [MessageEndpointFormFields.SASL_MECHANISM] : {
      ...notRequired,
      name  : MessageEndpointFormFields.SASL_MECHANISM,
      value : kafkaProducerProperties?.['sasl.mechanism'] || ''
    },
    [MessageEndpointFormFields.ACKNOWLEDGMENTS] : {
      ...notRequired,
      name  : MessageEndpointFormFields.ACKNOWLEDGMENTS,
      value : kafkaProducerProperties?.acks || ''
    },
    [MessageEndpointFormFields.SASL_JAAS_CONFIG] : {
      ...notRequired,
      name  : MessageEndpointFormFields.SASL_JAAS_CONFIG,
      value : kafkaProducerProperties?.['sasl.jaas.config'] || ''
    },
    [MessageEndpointFormFields.KEY_SERIALIZER] : {
      ...notRequired,
      name  : MessageEndpointFormFields.KEY_SERIALIZER,
      value : kafkaProducerProperties?.['key.serializer'] || ''
    },
    [MessageEndpointFormFields.CLIENT_ID] : {
      ...notRequired,
      name  : MessageEndpointFormFields.CLIENT_ID,
      value : kafkaProducerProperties?.['client.id'] || ''
    }
  };
};

export const getInitialGCPPubSubFormState = (
  currentType: MessageEndpointTypes,
  exisitingMessageEndpoint?: MessageEndpointConfiguration
): FormState => {
  const isEdit   = !!exisitingMessageEndpoint;
  
  return {
    [MessageEndpointFormFields.GCP_TOPIC_NAME] : {
      ...required,
      name  : MessageEndpointFormFields.GCP_TOPIC_NAME,
      value : exisitingMessageEndpoint?.topicName || ''
    },
    [MessageEndpointFormFields.PRIVATE_KEY_ID] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.PRIVATE_KEY_ID,
      value    : ''
    },
    [MessageEndpointFormFields.CERT_URL] : {
      ...notRequired,
      name  : MessageEndpointFormFields.CERT_URL,
      value : exisitingMessageEndpoint?.certUrl || ''
    },
    [MessageEndpointFormFields.PROJECT_ID] : {
      ...required,
      name  : MessageEndpointFormFields.PROJECT_ID,
      value : exisitingMessageEndpoint?.projectId || ''
    },
    [MessageEndpointFormFields.CLIENT_EMAIL] : {
      ...required,
      name  : MessageEndpointFormFields.CLIENT_EMAIL,
      value : exisitingMessageEndpoint?.clientEmail || ''
    },
    [MessageEndpointFormFields.PRIVATE_KEY] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.PRIVATE_KEY,
      value    : exisitingMessageEndpoint?.privateKey || ''
    },
    [MessageEndpointFormFields.CLIENT_ID] : {
      required : !isEdit,
      name     : MessageEndpointFormFields.CLIENT_ID,
      value    : ''
    }
  };
};

// Serializes an Account's Message Endpoint for API consumption.
export const serializeMessageEndpoint = ({configuration, ...rest}: MessageEndpoint): MessageEndpoint => {
  const serializedConfiguration: MessageEndpointConfiguration         = {};
  const kafkaProducerProperties: KafkaProducerPropertiesConfiguration = {};

  for (const [key, obj] of Object.entries(configuration)) {
    if (!KAFKA_KEYS_MAP?.has(key)) {
      serializedConfiguration[key] = key === MessageEndpointFormFields.PRIVATE_KEY
        ? unescapePrivateKeyNewlines(obj?.value)
        : obj?.value;
    }

    if (KAFKA_KEYS_MAP?.has(key) && isNotNilOrEmpty(obj?.value)) {
      kafkaProducerProperties[key] = obj?.value;
    }
    if (isEqual(key, MessageEndpointFormFields.BOOTSTRAP_SERVERS) && isNotNilOrEmpty(obj?.value)) {
      kafkaProducerProperties['bootstrap.servers'] = obj?.value;
    }
  }

  removeSecureField(serializedConfiguration, 'awsSecretAccessKey');
  removeSecureField(serializedConfiguration, 'password');
  removeSecureField(serializedConfiguration, 'privateKeyId');
  removeSecureField(serializedConfiguration, 'privateKey');
  removeSecureField(serializedConfiguration, 'clientId');

  serializedConfiguration.kafkaProducerProperties = kafkaProducerProperties;

  return {...rest, configuration: serializedConfiguration} as MessageEndpoint;
};

const removeSecureField = (configuration: MessageEndpointConfiguration, field): MessageEndpointConfiguration => {
  const value = configuration?.[field];
  if (!value?.length) {
    delete configuration[field];
  }
  return configuration;
};

// Validates the Message Endpoint fields & updates the state with any found.
export const validateMessageEndpoint = (messageEndpointFormState: FormState) => {
  const errors = {};
  for (const [name, value] of Object.entries(messageEndpointFormState)) {
    const isEmailField   = name === MessageEndpointFormFields.CLIENT_EMAIL;
    const hasEmailError  = isEmailField && !isEmpty(value.value) && !isValidEmail(value.value);
    if ((value?.required && isEmpty(value.value)) || hasEmailError || value?.error) {
      errors[name] = {...value, error: true};
    }
  }
  return errors;
};

// Returns the respective message endpoint name for the given message endpoint type.
export const getMessageName = (type: MessageEndpointTypes): MessageEndpointNames => {
  return type === MessageEndpointTypes.AWS_KINESIS
    ? MessageEndpointNames.STREAM_NAME : MessageEndpointNames.QUEUE_NAME;
};

// Returns the respective message endpoint type (returning Kinesis as the default if not present).
export const getMessageType = (formState: FormState): MessageEndpointTypes => {
  return MessageEndpointTypes[formState[MessageEndpointFormFields.TYPE]?.value] || MessageEndpointTypes.AWS_KINESIS;
};

// Returns the boolean value for the given Kafka message endpoint type.
export const getIsKafkaType = (type: MessageEndpointTypes): boolean => (
  type === MessageEndpointTypes.KAFKA_MTLS ||
  type === MessageEndpointTypes.KAFKA_SASL_PLAIN ||
  type === MessageEndpointTypes.KAFKA_SASL_SCRAM ||
  false
);

export const getIsStreamType = (type: MessageEndpointTypes): boolean => {
  const {AWS_KINESIS, AWS_SQS} = MessageEndpointTypes;
  return [AWS_KINESIS, AWS_SQS].includes(type) || false;
};

// Returns the boolean value for the given Kafka Plain Or Scram message endpoint type.
export const getIsKafkaPlainOrScramType = (type: MessageEndpointTypes): boolean => (
  type === MessageEndpointTypes.KAFKA_SASL_PLAIN ||
  type === MessageEndpointTypes.KAFKA_SASL_SCRAM ||
  false
);

// Returns true if the given value is neither null/undefined nor empty (e.g., empty array or empty object).
export const isNotNilOrEmpty = (value): boolean => {
  return !isNil(value) && !isEmpty(value);
};

export const getIsGCPPubSubType = (type: MessageEndpointTypes): boolean => {
  const {GCP_PUBSUB} = MessageEndpointTypes;
  return [GCP_PUBSUB].includes(type) || false;
};

// Collapses repeated escaped newline sequences (\n, \\n, ...) in a pasted GCP private key down to a
// single \n. A real line break here would be silently stripped by the single-line input, so the escaped
// form is kept until serializeMessageEndpoint converts it to a real line break for the API payload.
export const sanitizePrivateKeyValue = (value: string): string => (
  value ? value.replace(/\\+n/g, '\\n') : value
);

// Converts the literal \n sequence in a pasted GCP private key back to a real line break before
// the private key is sent in the API payload.
const unescapePrivateKeyNewlines = (value?: string): string => value?.replaceAll?.('\\n', '\n');
